import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ScanBarcode,
  QrCode,
  Search,
  ShieldCheck,
  Link2,
  Boxes,
  ArrowRight,
  Smartphone,
  Zap,
  Lock,
} from "lucide-react";
import { SiteLayout } from "@/components/layout/SiteLayout";
import { ContentBlock } from "@/components/layout/ContentBlock";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TOOL_ROUTES } from "@/lib/routes";
import { pageHead } from "@/lib/seo";
import { FaqSection } from "@/components/seo/FaqSection";
import {
  Camera,
  Upload,
  Sparkles,
  Store,
  Warehouse,
  Code2,
  BookOpen,
  PackageSearch,
  CheckCircle2,
  Wifi,
} from "lucide-react";

export const Route = createFileRoute("/")({
  component: Index,
  head: () =>
    pageHead({
      title: "Read Barcode Online | Free Barcode Reader & Product Lookup",
      description:
        "Read any barcode online — UPC, EAN, GTIN, ISBN, QR and Data Matrix. Free browser-based scanner, lookup and GS1 Digital Link decoder.",
      path: "/",
    }),
});

const FAQ = [
  {
    q: "Is ReadBarcode.com really free?",
    a: "Yes. Every tool on ReadBarcode.com is free to use, with no signup required. Scanning and decoding run in your browser whenever possible, so we never see your images.",
  },
  {
    q: "Which Barcode Formats Are Supported?",
    a: "ReadBarcode supports the formats real retailers and logistics teams use: UPC-A, UPC-E, EAN-8, EAN-13, GTIN-14, ISBN-10, ISBN-13, Code 128, Code 39, ITF, QR Code, Data Matrix and GS1 Digital Link URLs.",
  },
  {
    q: "Can I scan a barcode with my phone camera?",
    a: "Yes. Open the Online Barcode Reader on your phone, allow camera access, and point it at the barcode. No app install is needed — it runs in the mobile browser.",
  },
  {
    q: "Can I read a barcode from an image or screenshot?",
    a: "Yes. Use Read Barcode From Image to upload a photo or screenshot. The decoder will try to extract the barcode value and tell you the detected format.",
  },
  {
    q: "Does ReadBarcode look up product information?",
    a: "ReadBarcode validates UPC, EAN, GTIN and ISBN codes locally. Product lookups depend on the data we can connect to and are clearly marked when available.",
  },
  {
    q: "What Is GS1 Digital Link?",
    a: "GS1 Digital Link encodes a GTIN — plus optional batch, serial and expiry data — inside a web URL, usually inside a QR code or Data Matrix. Our GS1 Digital Link Decoder parses these URLs into structured fields.",
  },
  {
    q: "Can My Phone Read Barcodes?",
    a: "Yes. Any phone with a camera and a current browser can read barcodes here without an app. Open the Camera Barcode Scanner, allow camera access and point the rear camera at the code.",
  },
  {
    q: "Can Humans Read Barcodes?",
    a: "Partly. People read the digits printed under most retail barcodes, and a UPC-A can in principle be decoded from its bar and space widths, but that is slow and easy to get wrong. QR codes and other 2D codes need a camera or scanner.",
  },
  {
    q: "Is My Barcode Readable?",
    a: "Scan it with the camera scanner or upload a sharp image of it: if it decodes, a reader can read it, and the GTIN Validator confirms that a retail number has a correct check digit. A formal print-quality grade needs a barcode verifier, which measures a 1D symbol against ISO/IEC 15416 and a 2D symbol against ISO/IEC 15415.",
  },
];

const FEATURE_TOOLS = [
  {
    path: "/barcode-reader",
    title: "Camera Barcode Scanner",
    desc: "Scan with your phone or webcam and decode any 1D or 2D barcode instantly.",
    icon: Camera,
    cta: "Start scanning",
  },
  {
    path: "/read-barcode-from-image",
    title: "Read Barcode From Image",
    desc: "Drop a photo or screenshot — we'll extract the barcode value in seconds.",
    icon: Upload,
    cta: "Upload image",
  },
  {
    path: "/qr-code-reader",
    title: "QR Code Reader",
    desc: "Decode QR codes safely — URLs, Wi‑Fi credentials, text and contacts.",
    icon: QrCode,
    cta: "Open QR reader",
  },
  {
    path: "/upc-lookup",
    title: "UPC, EAN & ISBN Lookup",
    desc: "Validate retail and book barcodes with proper check‑digit checks.",
    icon: Search,
    cta: "Look up a code",
  },
  {
    path: "/gtin-validator",
    title: "GTIN Validator & Check Digit",
    desc: "Verify GTIN‑8/12/13/14 numbers and calculate missing check digits.",
    icon: ShieldCheck,
    cta: "Validate GTIN",
  },
  {
    path: "/gs1-digital-link-decoder",
    title: "GS1 Digital Link Tools",
    desc: "Parse GS1 Digital Link URLs into GTIN, lot, serial and expiry fields.",
    icon: Link2,
    cta: "Decode GS1",
  },
];

const SUPPORTED_FORMATS = [
  "UPC-A",
  "UPC-E",
  "EAN-8",
  "EAN-13",
  "GTIN-14",
  "ISBN-10",
  "ISBN-13",
  "Code 128",
  "Code 39",
  "ITF",
  "QR Code",
  "Data Matrix",
  "GS1 Digital Link",
];

const HOW_IT_WORKS = [
  {
    step: "1",
    title: "Pick a Tool",
    desc: "Choose the scanner, image reader, lookup or validator that fits your task.",
    icon: Sparkles,
  },
  {
    step: "2",
    title: "Scan, Upload or Paste",
    desc: "Use your camera, drop an image, or paste a barcode number — whatever's fastest.",
    icon: Camera,
  },
  {
    step: "3",
    title: "Get a Clean Result",
    desc: "See the decoded value, detected format and validation status. Copy or export in one click.",
    icon: CheckCircle2,
  },
];

const AUDIENCES = [
  {
    title: "E‑Commerce Sellers",
    desc: "Confirm GTINs before listing on Amazon, eBay or Shopify and avoid suppressed listings.",
    icon: Store,
    link: { label: "Validate a GTIN", path: "/gtin-validator" },
  },
  {
    title: "Retail & Warehouse Teams",
    desc: "Scan inbound stock with a phone — no scanner gun, no app install.",
    icon: Warehouse,
    link: { label: "Try inventory scanner", path: "/inventory-scanner" },
  },
  {
    title: "Developers",
    desc: "Decode samples, debug GS1 Digital Link URLs and check digit algorithms in seconds.",
    icon: Code2,
    link: { label: "Decode GS1 Digital Link", path: "/gs1-digital-link-decoder" },
  },
  {
    title: "Researchers & Librarians",
    desc: "Read ISBNs from book covers and validate them before adding to catalogs.",
    icon: BookOpen,
    link: { label: "Read an ISBN", path: "/isbn-barcode-reader" },
  },
  {
    title: "Small Businesses",
    desc: "Run a small stock list from your browser — no SaaS subscription required.",
    icon: PackageSearch,
    link: { label: "Open inventory tool", path: "/inventory-scanner" },
  },
  {
    title: "Anyone With a Barcode",
    desc: "Stuck with a label you can't read? Snap it and let the reader do the work.",
    icon: ScanBarcode,
    link: { label: "Read from image", path: "/read-barcode-from-image" },
  },
];

function Index() {
  return (
    <SiteLayout>
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 -z-10 opacity-60"
          style={{
            background:
              "radial-gradient(60% 50% at 50% 0%, oklch(0.95 0.05 263) 0%, transparent 70%)",
          }}
        />
        <div className="mx-auto max-w-6xl px-4 py-16 md:py-24">
          <div className="mx-auto max-w-3xl text-center">
            <Badge variant="secondary" className="mb-4">
              Free · No signup · Runs in your browser
            </Badge>
            <h1 className="text-balance text-4xl font-semibold tracking-tight md:text-6xl">
              Read any barcode online — UPC, EAN, GTIN, QR and GS1
            </h1>
            <p className="mt-5 text-balance text-lg text-muted-foreground md:text-xl">
              Scan with your camera, upload an image, or paste a code. ReadBarcode.com decodes,
              validates and looks up barcodes in your browser — free, with no signup and nothing
              installed. Decoding covers 1D codes such as UPC, EAN and Code 128 and 2D codes such as
              QR, Data Matrix and PDF417.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Button asChild size="lg">
                <a href="/barcode-reader">
                  <Camera className="mr-2 h-4 w-4" />
                  Scan with camera
                </a>
              </Button>
              <Button asChild size="lg" variant="outline">
                <a href="/read-barcode-from-image">
                  <Upload className="mr-2 h-4 w-4" />
                  Upload an image
                </a>
              </Button>
              <Button asChild size="lg" variant="outline">
                <a href="/upc-lookup">
                  <Search className="mr-2 h-4 w-4" />
                  Look up a barcode
                </a>
              </Button>
            </div>
            <div className="mt-4 flex flex-wrap justify-center gap-3 text-sm">
              <a href="/gtin-validator" className="text-primary hover:underline">
                Validate a GTIN
              </a>
              <span className="text-muted-foreground">·</span>
              <a href="/gs1-digital-link-decoder" className="text-primary hover:underline">
                Decode GS1 Digital Link
              </a>
              <span className="text-muted-foreground">·</span>
              <a href="/qr-code-reader" className="text-primary hover:underline">
                Read a QR code
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Tool entry cards */}
      <section className="mx-auto max-w-6xl px-4 py-16">
        <div className="mb-8 flex items-end justify-between gap-4">
          <div>
            <h2 className="text-2xl font-semibold tracking-tight md:text-3xl">
              All Your Barcode Tools in One Place
            </h2>
            <p className="mt-2 text-muted-foreground">
              Purpose-built utilities for every step of a barcode workflow.
            </p>
          </div>
          <a
            href="/barcode-guides"
            className="hidden text-sm text-primary hover:underline md:inline"
          >
            Browse guides →
          </a>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURE_TOOLS.map((t) => (
            <a key={t.path} href={t.path} className="group">
              <Card className="h-full transition-all hover:border-primary/40 hover:shadow-md">
                <CardContent className="p-6">
                  <div className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-md bg-primary/10 text-primary">
                    <t.icon className="h-5 w-5" />
                  </div>
                  <h3 className="text-base font-semibold">{t.title}</h3>
                  <p className="mt-1.5 text-sm text-muted-foreground">{t.desc}</p>
                  <div className="mt-4 inline-flex items-center text-sm font-medium text-primary">
                    {t.cta}
                    <ArrowRight className="ml-1 h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                  </div>
                </CardContent>
              </Card>
            </a>
          ))}
        </div>
      </section>

      {/* Trust / utility */}
      <section className="border-y border-border bg-muted/30">
        <div className="mx-auto grid max-w-6xl gap-8 px-4 py-14 md:grid-cols-3">
          <TrustItem
            icon={Zap}
            title="Instant results"
            desc="Decoding runs locally in your browser when possible — no waiting on uploads."
          />
          <TrustItem
            icon={Lock}
            title="Private by default"
            desc="Images and codes stay on your device. We don't store what you scan."
          />
          <TrustItem
            icon={Smartphone}
            title="Works on mobile"
            desc="Use the camera on your phone or tablet — no app install needed."
          />
        </div>
      </section>

      {/* Generators */}
      <section className="mx-auto max-w-6xl px-4 py-16">
        <div className="mb-8 max-w-2xl">
          <Badge variant="secondary" className="mb-3">
            Generators
          </Badge>
          <h2 className="text-2xl font-semibold tracking-tight md:text-3xl">
            Create Barcodes and QR Codes in Your Browser
          </h2>
          <p className="mt-2 text-muted-foreground">
            Generate retail-ready 1D barcodes or fully customizable QR codes — no signup, no
            watermark. Download as PNG or SVG.
          </p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <a href="/barcode-generator" className="group">
            <Card className="h-full transition-all hover:border-primary/40 hover:shadow-md">
              <CardContent className="p-6">
                <div className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-md bg-primary/10 text-primary">
                  <ScanBarcode className="h-5 w-5" />
                </div>
                <h3 className="text-base font-semibold">Barcode Generator</h3>
                <p className="mt-1.5 text-sm text-muted-foreground">
                  Generate UPC, EAN, Code 128, Code 39, ITF-14 and Codabar with proper check digits.
                </p>
                <div className="mt-4 inline-flex items-center text-sm font-medium text-primary">
                  Open generator
                  <ArrowRight className="ml-1 h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                </div>
              </CardContent>
            </Card>
          </a>
          <a href="/qr-code-generator" className="group">
            <Card className="h-full transition-all hover:border-primary/40 hover:shadow-md">
              <CardContent className="p-6">
                <div className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-md bg-primary/10 text-primary">
                  <QrCode className="h-5 w-5" />
                </div>
                <h3 className="text-base font-semibold">QR Code Generator</h3>
                <p className="mt-1.5 text-sm text-muted-foreground">
                  Build QR codes with custom error correction, size and foreground/background
                  colors.
                </p>
                <div className="mt-4 inline-flex items-center text-sm font-medium text-primary">
                  Open generator
                  <ArrowRight className="ml-1 h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                </div>
              </CardContent>
            </Card>
          </a>
          <a href="/wifi-qr-code-generator" className="group">
            <Card className="h-full transition-all hover:border-primary/40 hover:shadow-md">
              <CardContent className="p-6">
                <div className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-md bg-primary/10 text-primary">
                  <Wifi className="h-5 w-5" />
                </div>
                <h3 className="text-base font-semibold">Wi-Fi QR Code Generator</h3>
                <p className="mt-1.5 text-sm text-muted-foreground">
                  Turn your Wi-Fi network into a one-scan QR code — guests join without typing the
                  password.
                </p>
                <div className="mt-4 inline-flex items-center text-sm font-medium text-primary">
                  Open generator
                  <ArrowRight className="ml-1 h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                </div>
              </CardContent>
            </Card>
          </a>
        </div>
        <div className="mt-6">
          <a
            href="/generators"
            className="inline-flex items-center text-sm font-medium text-primary hover:underline"
          >
            View all generators
            <ArrowRight className="ml-1 h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
          </a>
        </div>
      </section>

      {/* How it works */}
      <section className="mx-auto max-w-6xl px-4 py-16">
        <div className="max-w-2xl">
          <h2 className="text-2xl font-semibold tracking-tight md:text-3xl">
            How ReadBarcode works
          </h2>
          <p className="mt-2 text-muted-foreground">
            Every tool follows the same three steps — so once you learn one, you know them all.
          </p>
        </div>
        <ol className="mt-8 grid gap-4 md:grid-cols-3">
          {HOW_IT_WORKS.map((s) => (
            <li key={s.step} className="rounded-xl border border-border bg-card p-6">
              <div className="flex items-center gap-3">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground">
                  {s.step}
                </span>
                <s.icon className="h-4 w-4 text-muted-foreground" />
              </div>
              <h3 className="mt-4 text-base font-semibold">{s.title}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{s.desc}</p>
            </li>
          ))}
        </ol>
      </section>

      {/* Who uses this */}
      <section className="border-y border-border bg-muted/30">
        <div className="mx-auto max-w-6xl px-4 py-16">
          <div className="max-w-2xl">
            <h2 className="text-2xl font-semibold tracking-tight md:text-3xl">
              Who uses ReadBarcode
            </h2>
            <p className="mt-2 text-muted-foreground">
              Built for anyone who has to read, check or move barcoded items — not just developers.
            </p>
          </div>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {AUDIENCES.map((a) => (
              <div key={a.title} className="rounded-xl border border-border bg-background p-6">
                <div className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-md bg-primary/10 text-primary">
                  <a.icon className="h-5 w-5" />
                </div>
                <h3 className="text-base font-semibold">{a.title}</h3>
                <p className="mt-1.5 text-sm text-muted-foreground">{a.desc}</p>
                <a
                  href={a.link.path}
                  className="mt-4 inline-flex items-center text-sm font-medium text-primary hover:underline"
                >
                  {a.link.label}
                  <ArrowRight className="ml-1 h-3.5 w-3.5" />
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Supported formats */}
      <section className="mx-auto max-w-6xl px-4 py-16">
        <div className="grid gap-10 md:grid-cols-[1fr_1.2fr]">
          <div>
            <h2 className="text-2xl font-semibold tracking-tight md:text-3xl">
              Supported Barcode Formats
            </h2>
            <p className="mt-2 text-muted-foreground">
              ReadBarcode covers the symbologies real businesses use — across retail, logistics,
              publishing and healthcare.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {SUPPORTED_FORMATS.map((f) => (
                <Badge key={f} variant="outline" className="px-3 py-1 text-sm">
                  {f}
                </Badge>
              ))}
            </div>
            <p className="mt-6 text-sm text-muted-foreground [&_a]:text-primary [&_a]:hover:underline">To make codes rather than read them, the <Link to="/itf-14-label-generator">ITF-14 label generator</Link> creates labels for outer cases, and the <Link to="/data-matrix-generator">Data Matrix generator</Link>, <Link to="/pdf417-generator">PDF417 generator</Link> and <Link to="/aztec-code-generator">Aztec code generator</Link> build 2D symbols in those three formats.</p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {TOOL_ROUTES.slice(0, 8).map((t) => (
              <a
                key={t.path}
                href={t.path}
                className="group flex items-start gap-3 rounded-lg border border-border bg-background p-4 transition-colors hover:border-primary/40"
              >
                <Boxes className="mt-0.5 h-4 w-4 text-primary" />
                <div>
                  <div className="text-sm font-medium group-hover:text-primary">{t.label}</div>
                  <div className="text-xs text-muted-foreground">{t.description}</div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Reading guides and FAQ */}
      <section className="border-t border-border bg-muted/30">
        <div className="mx-auto max-w-3xl px-4 py-16">
          <ContentBlock>
            <h2>How to Read a Barcode Online</h2>
          <p>To read a barcode online, open the camera scanner or the image reader in a current browser, point it at the code or drop in a picture, and the value appears with its format name. You can read a barcode online free here, with nothing to install and no account. It works as a free online barcode reader on phones and computers alike.</p>
          <p>If you already have digits and want to know whether they are a UPC, EAN, GTIN or ISBN, paste them into the <Link to="/barcode-type-detector">barcode type detector</Link>. Linear codes such as UPC, EAN and Code 128 hold a short string in one row of bars, while 2D codes such as QR and Data Matrix hold far more in a grid; the <Link to="/barcode-guides/1d-vs-2d-barcodes">guide to 1D vs 2D barcodes</Link> explains the trade-offs. Code 128, the alphanumeric code on most shipping labels, has its own <Link to="/barcode-guides/code-128-explained">Code 128 guide</Link>, and the <Link to="/barcode-guides/barcode-formats">barcode formats guide</Link> compares the rest.</p>
          <h3>Read a Barcode With Your Phone Camera</h3>
          <p>Open the <Link to="/barcode-reader">Camera Barcode Scanner</Link> on your phone, press Start camera and allow access when the browser asks. The scanner requests the rear camera at up to 1080p with continuous autofocus where the phone supports it, and it stops as soon as one code is read.</p>
          <h3>Read a Barcode From a Picture</h3>
          <p>To read a barcode from a picture you already have, open <Link to="/read-barcode-from-image">Read Barcode From Image</Link> and drop in a photo, screenshot or scanned label. It is the quickest way to read a barcode image online when the file arrives by email or chat, and it works on a computer with no camera. If the first attempt finds nothing, the reader adds a white border around the image and tries again, which rescues codes cropped too tightly.</p>
          <h2>Barcode Lookup: What a Barcode Number Can Tell You</h2>
          <p>A barcode number identifies a trade item, not its name, price or maker's address. A barcode lookup works by matching that number against databases where the brand owner registered the product details.</p>
          <p>A retail barcode such as UPC-A or EAN-13 carries a GTIN, explained in full in <Link to="/barcode-guides/what-is-a-gtin">what a GTIN is</Link>. Knowing its three parts is how to read barcode numbers:</p>
          <ul>
            <li>GS1 Company Prefix: licensed to the brand owner by a GS1 Member Organization. Its first digits, the GS1 Prefix, show which organization issued it, not the country where the product was made.</li>
            <li>Item reference: chosen by the brand owner for each product variant.</li>
            <li>Check digit: the last digit, calculated from the others to catch misreads and typos, as shown in <Link to="/barcode-guides/check-digits-explained">how check digits work</Link>.</li>
          </ul>
          <p>Some prefixes have a fixed meaning. A 13-digit number starting with 978 or 979 is usually a book's ISBN, and numbers starting with 2 are restricted circulation numbers that a store assigns itself, often to weighed items, so no public database knows them.</p>
          <p>UPC and EAN are two forms of the same system: a 12-digit UPC-A becomes a valid EAN-13 by adding a leading zero, as the <Link to="/barcode-guides/upc-vs-ean">UPC vs EAN guide</Link> shows. Newer packs may also carry a QR code with a GS1 Digital Link URL, which puts the same GTIN in a web address; the <Link to="/barcode-guides/gs1-digital-link">GS1 Digital Link guide</Link> shows how to read one.</p>
          <h2>How to Read a Barcode Without a Scanner</h2>
          <p>You can read a barcode without a scanner by typing the digits printed under the bars, or by using the camera of a phone or laptop in the browser.</p>
          <p>Type the printed digits into the <Link to="/gtin-validator">GTIN Validator</Link> to check the check digit, or into the <Link to="/upc-lookup">UPC lookup</Link> to look the number up. Reading the bars directly is possible for a UPC-A, where each digit takes seven modules drawn as two bars and two spaces, but it is slow and error-prone.</p>
            <FaqSection
              heading="Frequently Asked Questions"
              description="Common questions about reading, validating and decoding barcodes online."
              items={FAQ}
            />
          </ContentBlock>

          <RelatedGuides />

          <div className="mt-8 rounded-xl border border-border bg-background p-6">
            <h3 className="text-base font-semibold">Ready to read your first barcode?</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Jump straight into the camera scanner or upload an image — no signup needed.
            </p>
            <div className="mt-4 flex flex-wrap gap-3">
              <Button asChild>
                <a href="/barcode-reader">
                  <Camera className="mr-2 h-4 w-4" />
                  Open the scanner
                </a>
              </Button>
              <Button asChild variant="outline">
                <a href="/barcode-guides">
                  <BookOpen className="mr-2 h-4 w-4" />
                  Read the guides
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}

const RELATED_LINKS = [
  { path: "/barcode-guides/what-is-a-gtin", label: "What Is a GTIN?" },
  { path: "/barcode-guides/barcode-formats", label: "Barcode formats guide" },
  { path: "/barcode-guides/upc-vs-ean", label: "UPC vs EAN" },
  { path: "/barcode-guides/1d-vs-2d-barcodes", label: "1D vs 2D barcodes" },
  { path: "/barcode-guides/code-128-explained", label: "Code 128 explained" },
  { path: "/barcode-guides/check-digits-explained", label: "Check digits explained" },
  { path: "/barcode-guides/gs1-digital-link", label: "GS1 Digital Link guide" },
  { path: "/barcode-type-detector", label: "Barcode type detector" },
  { path: "/data-matrix-generator", label: "Data Matrix generator" },
  { path: "/pdf417-generator", label: "PDF417 generator" },
  { path: "/aztec-code-generator", label: "Aztec code generator" },
  { path: "/itf-14-label-generator", label: "ITF-14 label generator" },
];

function RelatedGuides() {
  return (
    <section className="mt-12">
      <h2 className="text-2xl font-semibold tracking-tight">Barcode Guides and More Tools</h2>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        {RELATED_LINKS.map((r) => (
          <a
            key={r.path}
            href={r.path}
            className="rounded-lg border border-border bg-background p-4 text-sm font-medium transition-colors hover:border-primary/40 hover:text-primary"
          >
            {r.label}
          </a>
        ))}
      </div>
    </section>
  );
}

function TrustItem({ icon: Icon, title, desc }: { icon: typeof Zap; title: string; desc: string }) {
  return (
    <div className="flex gap-4">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-background text-primary ring-1 ring-border">
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <div className="font-semibold">{title}</div>
        <p className="mt-1 text-sm text-muted-foreground">{desc}</p>
      </div>
    </div>
  );
}
